package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PrimaryScrollableTabRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RhAbsenceColumn
import br.com.vvrefeicoes.portal.domain.model.RhCostsColumn
import br.com.vvrefeicoes.portal.domain.model.RhPayrollMassColumn
import br.com.vvrefeicoes.portal.domain.model.RhSheetColumn
import br.com.vvrefeicoes.portal.domain.model.RhSheetKind
import br.com.vvrefeicoes.portal.domain.model.payrollMassReportToGrid
import br.com.vvrefeicoes.portal.feature.operacional.MonthPeriodSelector
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import com.composables.icons.lucide.Building
import com.composables.icons.lucide.Lucide
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_entries_unit_label
import portalvv.shared.generated.resources.rh_entries_title
import portalvv.shared.generated.resources.rh_entries_unit_placeholder
import portalvv.shared.generated.resources.rh_tab_absenteismo
import portalvv.shared.generated.resources.rh_tab_custos
import portalvv.shared.generated.resources.rh_tab_massa_salarial

private val RH_FILTERS_MIN_WIDTH = 720.dp

/**
 * Os documentos que o RH importa por mês: a planilha de custos (que já traz salário base e
 * folha líquida de cada funcionário), o relatório de absenteísmo do sistema de ponto e o resumo
 * da folha com a massa salarial de cada departamento.
 */
private enum class RhEntryType(
    val title: StringResource,
    val kind: RhSheetKind,
    val columns: List<RhSheetColumn>,
    val normalizeGrid: (List<List<String>>) -> List<List<String>> = { it },
) {
    CUSTOS_RH(Res.string.rh_tab_custos, RhSheetKind.CUSTOS_RH, RhCostsColumn.entries),
    ABSENTEISMO(Res.string.rh_tab_absenteismo, RhSheetKind.ABSENTEISMO, RhAbsenceColumn.entries),
    MASSA_SALARIAL(
        Res.string.rh_tab_massa_salarial,
        RhSheetKind.MASSA_SALARIAL,
        RhPayrollMassColumn.entries,
        ::payrollMassReportToGrid,
    ),
}

@Composable
fun RhEntriesScreen() {
    var selectedType by remember { mutableStateOf(RhEntryType.CUSTOS_RH) }
    var selectedMonth by remember { mutableStateOf(SystemDateTimeProvider().currentPeriod()) }
    val filters = LocalGlobalFilters.current
    var selectedUnit by remember { mutableStateOf<CompanyRef?>(null) }

    // O filtro de unidades obedece a header (diretoria/gestão): reflete a unidade selecionada e
    // reseta quando ela sai do escopo da header.
    LaunchedEffect(filters.unit, filters.units) {
        val headerUnit = filters.selectedUnit
        selectedUnit =
            when {
                headerUnit != null -> headerUnit
                selectedUnit?.let { current -> filters.units.any { it.name == current.name } } == true -> selectedUnit
                else -> filters.units.singleOrNull()
            }
    }

    val unitPlaceholder = stringResource(Res.string.rh_entries_unit_placeholder)
    val unitOptions = listOf(unitPlaceholder) + filters.units.map { it.name }

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        footer = { AppFooter() },
    ) {
        Text(
            text = stringResource(Res.string.rh_entries_title),
            style = MaterialTheme.typography.headlineMedium,
        )

        RhFiltersCard(
            unitOptions = unitOptions,
            selectedUnit = selectedUnit,
            unitPlaceholder = unitPlaceholder,
            onSelectUnit = { name ->
                selectedUnit = if (name == unitPlaceholder) null else filters.units.firstOrNull { it.name == name }
            },
            month = selectedMonth,
            onMonthChange = { selectedMonth = it },
        )

        PrimaryScrollableTabRow(selectedTabIndex = selectedType.ordinal) {
            RhEntryType.entries.forEach { type ->
                Tab(
                    selected = type == selectedType,
                    onClick = { selectedType = type },
                    text = { Text(text = stringResource(type.title)) },
                )
            }
        }

        // Cada planilha tem o seu próprio estado: sem a chave, trocar de aba reaproveitaria o da anterior.
        key(selectedType) {
            RhSheetTab(
                month = selectedMonth,
                kind = selectedType.kind,
                columns = selectedType.columns,
                selectedUnit = selectedUnit?.name,
                normalizeGrid = selectedType.normalizeGrid,
            )
        }
    }
}

@Composable
private fun RhFiltersCard(
    unitOptions: List<String>,
    selectedUnit: CompanyRef?,
    unitPlaceholder: String,
    onSelectUnit: (String) -> Unit,
    month: MonthPeriod,
    onMonthChange: (MonthPeriod) -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            if (maxWidth >= RH_FILTERS_MIN_WIDTH) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                        UnitFilterLabel()
                        UnitFilterSelect(unitOptions, selectedUnit, unitPlaceholder, onSelectUnit)
                    }
                    MonthPeriodSelector(month = month, onMonthChange = onMonthChange)
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
                    verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                        UnitFilterLabel()
                        UnitFilterSelect(unitOptions, selectedUnit, unitPlaceholder, onSelectUnit)
                    }
                    MonthPeriodSelector(month = month, onMonthChange = onMonthChange)
                }
            }
        }
    }
}

@Composable
private fun UnitFilterLabel() {
    Text(
        text = stringResource(Res.string.operational_entries_unit_label),
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

@Composable
private fun UnitFilterSelect(
    unitOptions: List<String>,
    selectedUnit: CompanyRef?,
    unitPlaceholder: String,
    onSelectUnit: (String) -> Unit,
) {
    SearchableSelect(
        leadingIcon = Lucide.Building,
        options = unitOptions,
        selected = selectedUnit?.name ?: unitPlaceholder,
        onSelect = onSelectUnit,
        modifier = Modifier.fillMaxWidth(),
        underlined = true,
    )
}
